import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Select } from '@/components/UberForm'
import { getSystemEnvironments } from '@/endpoints'
import { useApi } from '@/endpoints/hooks'
import { useAppContext } from '@/hooks'
import { DeploymentDto } from '@/rtkApi/mddApiSliceGenerated'
import { toLocaleString } from '@/utils/numbers'

import { useDetailTabContext } from '../context/DetailTabContext'
import { ActionTypeEnum } from '../context/types'

const MIN_ENV_SELECT_CHARS = 10
const MAX_ENV_SELECT_CHARS = 20
const SELECT_PADDING_CHARS = 4

type Props = {
	deployments?: DeploymentDto[],
	replacing: boolean
}

export const SelectEnv = ({ replacing, deployments }: Props) => {
	const { t } = useAppContext()
	const refInitEnvId = useRef(false)

	const {
		state: { systemNodeId, envId },
		dispatch,
	} = useDetailTabContext()

	const envs = useApi(getSystemEnvironments(systemNodeId))

	const envSelectWidth = useMemo(() => {
		if (!envs.data?.length) return `${MIN_ENV_SELECT_CHARS + SELECT_PADDING_CHARS}ch`
		const longestName = Math.max(...envs.data.map((env) => (env.name?.length ?? 0)))
		const width = Math.min(Math.max(longestName, MIN_ENV_SELECT_CHARS), MAX_ENV_SELECT_CHARS)
		return `${width + SELECT_PADDING_CHARS}ch`
	}, [envs.data])

	const currentDeployment = useMemo(() => {
		if (!deployments || !envId) return undefined
		return deployments.find((d) => d.environmentFeId === envId)
	}, [deployments, envId])

	useEffect(() => {
		return () => {
			dispatch({
				type: ActionTypeEnum.DETAIL_TAB_UPDATE,
				update: {
					envId: undefined,
				},
			})
		}
	}, [dispatch])

	useEffect(() => {
		if (refInitEnvId.current) {
			return
		}

		if (envs.data) {
			refInitEnvId.current = true

			dispatch({
				type: ActionTypeEnum.DETAIL_TAB_UPDATE,
				update: {
					envId: envs.data.find((env) => env.production)?.frontendId,
				},
			})
		}
	}, [dispatch, envs.data])

	return (
		<>
			<DeploymentInfo>
				{currentDeployment ? (
					<>
						{t('DEPLOYMENT_DATE')}:&nbsp;
						{toLocaleString(currentDeployment.created)}
					</>
				) : (
					<>{t('NO_DEPLOYMENT_FOR_SELECTED_ENV')}</>
				)}
			</DeploymentInfo>
			<EnvSelectWrapper style={{ width: envSelectWidth }}>
				<Select
					name="env"
					value={envId}
					options={envs.data}
					loading={envs.loading}
					valueKey="frontendId"
					labelKey="name"
					clearable={false}
					isNumeric
					onChange={(value) => {
						dispatch({
							type: ActionTypeEnum.DETAIL_TAB_UPDATE,
							update: {
								envId: value,
							},
						})
					}}
					disabled={replacing}
					testId="history-environment"
				/>
			</EnvSelectWrapper>
		</>
	)
}

const DeploymentInfo = styled.div`
	display: flex;
	align-items: center;
	margin-right: 6px;
	white-space: nowrap;
`

const EnvSelectWrapper = styled.div`
	margin-right: 5px;
`
